import {
  Modal,
  ModalBody,
  Tabs,
  TabsList,
  TabsListItem,
  TabsPanel
} from '@coolblue-development/freddy';
import selectProductSpecificationsTabTrigger from '@coolblue-development/ga4-events/triggers/ProductSpecifications/selectProductSpecificationsTabTrigger';
import { useAnalytics } from '@coolblue-development/next-analytics/client';
import { useFreddyTranslations } from '@coolblue-development/next-design-system-utils/client';
import { SquareProductImage } from '@coolblue-development/next-patterns/client';
import type { PropertyGroup, SubProducts } from '@features/product/typings';
import useIntersectionObserver from '@hooks/useIntersectionObserver';
import { useTranslation } from '@i18n/useTranslation';
import React, { useState } from 'react';

import { SearchField, SearchProvider, SearchResult } from './Search';
import { ModalContainer, StickyBar } from './SpecificationsModal.styled';

type SpecificationsModalProps = {
  productId: number;
  open: boolean;
  onClose: () => void;
  propertyGroups: PropertyGroup[];
  productTypeId: number;
  subProducts?: SubProducts[];
};

type SingleProductSpecificationsProps = {
  productId: number;
  propertyGroups: PropertyGroup[];
  productTypeId: number;
  isIntersecting: boolean;
};

type ComposedProductSpecificationsProps = {
  products: NonNullable<NonNullable<SubProducts>['product']>[];
  isIntersecting: boolean;
};

export default function SpecificationsModal({
  productId,
  open,
  onClose,
  propertyGroups,
  productTypeId,
  subProducts = []
}: SpecificationsModalProps) {
  const { t } = useTranslation('webshop');
  const modalTranslations = useFreddyTranslations('modal');
  const { rootRef, targetRef, isIntersecting } = useIntersectionObserver({ isEnabled: open });
  const products = subProducts
    .map(subProduct => subProduct?.product ?? null)
    .filter(product => product !== null);

  return (
    <Modal
      size='large'
      open={open}
      onClose={onClose}
      placement='bottom'
      title={t('productPage.specifications')}
      translations={modalTranslations}
      body={
        <ModalBody ref={rootRef}>
          <div ref={targetRef} style={{ height: 0 }} />

          <ModalContainer as='search' title={t('productPage.specifications')}>
            {products.length > 0 ? (
              <ComposedProductSpecifications products={products} isIntersecting={isIntersecting} />
            ) : (
              <SingleProductSpecifications
                productId={productId}
                propertyGroups={propertyGroups}
                productTypeId={productTypeId}
                isIntersecting={isIntersecting}
              />
            )}
          </ModalContainer>
        </ModalBody>
      }
    />
  );
}

function SingleProductSpecifications({
  productId,
  propertyGroups,
  productTypeId,
  isIntersecting
}: SingleProductSpecificationsProps) {
  return (
    <SearchProvider>
      <StickyBar isActive={!isIntersecting}>
        <SearchField productId={productId} />
      </StickyBar>

      <SearchResult
        productTypeId={productTypeId}
        productId={productId}
        propertyGroups={propertyGroups}
      />
    </SearchProvider>
  );
}

function ComposedProductSpecifications({
  products,
  isIntersecting
}: ComposedProductSpecificationsProps) {
  const tabsTranslations = useFreddyTranslations('tabs');
  const [activeProductId, setActiveProductId] = useState(products[0].id);
  const { trackEvent } = useAnalytics();

  function handleTabChange(value: string) {
    setActiveProductId(Number(value));

    const params = {
      index: products.findIndex(product => product.id === Number(value)) + 1,
      total_indices: products.length,
      item_id: Number(value)
    };
    trackEvent(selectProductSpecificationsTabTrigger(params));
  }

  return (
    <SearchProvider>
      <Tabs
        translations={tabsTranslations}
        value={activeProductId?.toString()}
        onChange={value => handleTabChange(value)}
      >
        <StickyBar isActive={!isIntersecting} isComposed>
          <TabsList>
            {products.map(product => {
              return (
                <TabsListItem
                  key={product.id}
                  value={product.id.toString()}
                  startAdornment={
                    <SquareProductImage
                      imageId={product?.primaryImage.id}
                      background='transparent'
                      size='small'
                      alt='' // As the product name is displayed, the alt attribute is redundant here.
                      loading='lazy'
                    />
                  }
                >
                  {product?.name}
                </TabsListItem>
              );
            })}
          </TabsList>

          {activeProductId && <SearchField productId={activeProductId} />}
        </StickyBar>

        {products.map(product => {
          return (
            <TabsPanel key={product.id} value={product.id.toString()}>
              <SearchResult
                productTypeId={Number(product.productType.productTypeId)}
                productId={product.id}
                propertyGroups={product?.properties?.propertyGroups ?? []}
              />
            </TabsPanel>
          );
        })}
      </Tabs>
    </SearchProvider>
  );
}
